import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  Utensils,
  Wine,
  GlassWater,
  Grape,
  Music,
  PartyPopper,
} from "lucide-react";

const courseMeta = {
  meal: { icon: Utensils, accent: "#E50914", label: "The Meal" },
  drink: { icon: Wine, accent: "#F5B841", label: "The Cocktail" },
  mocktail: { icon: GlassWater, accent: "#5EC5A0", label: "Non-Alcoholic" },
};

function buildSteps(p) {
  const steps = [
    { kind: "intro" },
  ];
  const courses = [
    { type: "meal", name: p.meal_name, ingredients: p.meal_ingredients || [], recipe: p.meal_recipe || [] },
    { type: "drink", name: p.drink_name, ingredients: p.drink_ingredients || [], recipe: p.drink_recipe || [] },
    {
      type: "mocktail",
      name: p.non_alcoholic_drink_name,
      ingredients: p.non_alcoholic_drink_ingredients || [],
      recipe: p.non_alcoholic_drink_recipe || [],
    },
  ];
  courses.forEach((c) => {
    steps.push({ kind: "ingredients", ...c });
    c.recipe.forEach((text, i) =>
      steps.push({ kind: "step", type: c.type, name: c.name, text, index: i, total: c.recipe.length })
    );
  });
  steps.push({ kind: "outro" });
  return steps;
}

export const MovieNightMode = ({ pairing, open, onClose }) => {
  const steps = useMemo(() => (pairing ? buildSteps(pairing) : []), [pairing]);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (open) setI(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setI((v) => Math.min(v + 1, steps.length - 1));
      if (e.key === "ArrowLeft") setI((v) => Math.max(v - 1, 0));
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, steps.length, onClose]);

  if (!open || !pairing) return null;

  const step = steps[i];
  const progress = ((i + 1) / steps.length) * 100;
  const atStart = i === 0;
  const atEnd = i === steps.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black flex flex-col animate-fade-up"
      data-testid="movie-night-mode"
    >
      {/* ambient glow */}
      <div className="pointer-events-none absolute inset-0 opacity-40"
        style={{ background: "radial-gradient(ellipse at 50% 0%, #1a0406 0%, #000 60%)" }} />

      {/* top bar */}
      <div className="relative flex items-center justify-between px-5 sm:px-8 h-16 shrink-0">
        <div className="flex items-center gap-2 text-white/60">
          <Clapperboard className="w-5 h-5 text-[#E50914]" />
          <span className="text-sm uppercase tracking-[0.2em]">Movie Night · {pairing.movie_title}</span>
        </div>
        <button
          onClick={onClose}
          data-testid="mnm-close-btn"
          className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/70 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* progress */}
      <div className="relative h-1 bg-white/10 mx-5 sm:mx-8 rounded-full overflow-hidden shrink-0">
        <div
          className="h-full bg-[#E50914] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* content */}
      <div className="relative flex-1 flex items-center justify-center px-6 py-8 overflow-y-auto custom-scroll">
        <div key={i} className="max-w-2xl w-full text-center animate-fade-up">
          {step.kind === "intro" && (
            <>
              <p className="uppercase text-xs tracking-[0.3em] text-[#E50914] mb-4">Dim the lights</p>
              <h2 className="font-display text-5xl sm:text-6xl font-black mb-6">{pairing.movie_title}</h2>
              <p className="text-lg font-light text-white/70 leading-relaxed">
                {pairing.theme_description ||
                  "Let's cook, pour, and press play. We'll walk you through each step, one at a time."}
              </p>
              <p className="text-sm text-white/40 mt-8">
                Use the arrows (or ← →) to move through the evening.
              </p>
            </>
          )}

          {step.kind === "ingredients" && (
            <>
              <CourseHeader type={step.type} />
              <h2 className="font-display text-4xl font-bold mb-2">{step.name}</h2>
              <p className="uppercase text-xs tracking-[0.2em] text-white/40 mb-6">
                Gather your ingredients
              </p>
              <ul className="inline-flex flex-col gap-2.5 text-left">
                {step.ingredients.map((ing, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-lg text-white/85">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: courseMeta[step.type].accent }}
                    />
                    {ing}
                  </li>
                ))}
              </ul>
            </>
          )}

          {step.kind === "step" && (
            <>
              <CourseHeader type={step.type} />
              <p className="uppercase text-xs tracking-[0.2em] text-white/40 mb-6">
                {step.name} · Step {step.index + 1} of {step.total}
              </p>
              <p
                className="font-display text-3xl sm:text-4xl font-medium leading-snug"
                data-testid="mnm-step-text"
              >
                {step.text}
              </p>
            </>
          )}

          {step.kind === "outro" && (
            <>
              <PartyPopper className="w-14 h-14 text-[#F5B841] mx-auto mb-5" />
              <h2 className="font-display text-4xl sm:text-5xl font-black mb-6">
                Enjoy the show.
              </h2>
              {(pairing.wine_pairing || pairing.soundtrack_vibe) && (
                <div className="flex flex-col gap-4 max-w-lg mx-auto text-left">
                  {pairing.wine_pairing && (
                    <div className="flex gap-3 items-start">
                      <Grape className="w-5 h-5 text-[#c96a86] shrink-0 mt-0.5" />
                      <p className="text-white/80">{pairing.wine_pairing}</p>
                    </div>
                  )}
                  {pairing.soundtrack_vibe && (
                    <div className="flex gap-3 items-start">
                      <Music className="w-5 h-5 text-[#5EC5A0] shrink-0 mt-0.5" />
                      <p className="text-white/80">{pairing.soundtrack_vibe}</p>
                    </div>
                  )}
                </div>
              )}
              <Button
                onClick={onClose}
                data-testid="mnm-finish-btn"
                className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-12 px-10 mt-10"
              >
                Press Play
              </Button>
            </>
          )}
        </div>
      </div>

      {/* nav */}
      <div className="relative flex items-center justify-between px-5 sm:px-8 h-20 shrink-0">
        <Button
          onClick={() => setI((v) => Math.max(v - 1, 0))}
          disabled={atStart}
          variant="ghost"
          data-testid="mnm-prev-btn"
          className="rounded-full bg-white/5 hover:bg-white/15 text-white h-12 px-6 disabled:opacity-30"
        >
          <ChevronLeft className="w-5 h-5 mr-1" /> Back
        </Button>
        <span className="text-sm text-white/40">
          {i + 1} / {steps.length}
        </span>
        <Button
          onClick={() => setI((v) => Math.min(v + 1, steps.length - 1))}
          disabled={atEnd}
          data-testid="mnm-next-btn"
          className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-12 px-6 disabled:opacity-30"
        >
          Next <ChevronRight className="w-5 h-5 ml-1" />
        </Button>
      </div>
    </div>
  );
};

const CourseHeader = ({ type }) => {
  const meta = courseMeta[type];
  const Icon = meta.icon;
  return (
    <div className="flex items-center justify-center gap-2 mb-5">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center"
        style={{ backgroundColor: `${meta.accent}22` }}
      >
        <Icon className="w-5 h-5" style={{ color: meta.accent }} />
      </div>
      <span className="uppercase text-xs tracking-[0.2em]" style={{ color: meta.accent }}>
        {meta.label}
      </span>
    </div>
  );
};
